import React from 'react';
import ChatListItemDesign from './design/ChatListItemDesign';
import { useUserPresence } from '@/hooks/useUserPresence';

interface Profile {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Conversation {
  id: string;
  name: string | null;
  type: 'direct' | 'group';
  last_message_at: string | null;
  participants: {
    user_id: string;
    profiles: Profile;
  }[];
}

interface ChatListItemProps {
  conversation: Conversation;
  isSelected: boolean;
  currentUserId: string;
  onClick: () => void;
  getConversationName: (conversation: Conversation) => string;
  getConversationAvatar: (conversation: Conversation) => string | null;
}

const ChatListItem: React.FC<ChatListItemProps> = ({
  conversation,
  isSelected,
  currentUserId,
  onClick,
  getConversationName,
  getConversationAvatar
}) => {
  const { isUserOnline } = useUserPresence();
  
  // Per le chat dirette, trova l'altro utente per controllare se è online
  const getOtherUserId = (): string | null => {
    if (conversation.type === 'group') return null;
    
    const otherParticipant = conversation.participants.find(
      p => p.user_id !== currentUserId
    );
    return otherParticipant?.user_id || null;
  };
  
  const otherUserId = getOtherUserId();
  const isOnline = otherUserId ? isUserOnline(otherUserId) : false;
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('it-IT', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString('it-IT', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    }
  };

  return (
    <ChatListItemDesign
      name={getConversationName(conversation)}
      avatar={getConversationAvatar(conversation)}
      timestamp={conversation.last_message_at ? formatTimestamp(conversation.last_message_at) : undefined}
      isSelected={isSelected}
      isOnline={isOnline}
      isGroup={conversation.type === 'group'}
      onClick={onClick}
    />
  );
};

export default ChatListItem;